import { createContext, useEffect, useState, useMemo } from "react";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { themes, ThemeName } from "./themes";
import { settingsStore } from "../settings/store";
import { useSettingValue } from "../hooks/useSettings";
import i18n from "../i18n";

type ThemeContextType = {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
};

export const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // 사용자 설정(appearance.theme)을 따름. 설정 페이지 밖에서도 앱 시작 시 로드
  useEffect(() => {
    void settingsStore.init();
  }, []);
  const themeMode = useSettingValue("appearance.theme");
  const language = useSettingValue("appearance.language");

  useEffect(() => {
    if (i18n.language !== language) void i18n.changeLanguage(language);
  }, [language]);

  // ponytail: appearance.fontSize 미적용. body zoom은 100vh·AutoSizer 측정을 깨서 제거함 → rem 기반 폰트로 바꿀 때 다시 연결
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  const resolvedTheme: ThemeName = themeMode === "system" ? (prefersDark ? "dark" : "light") : themeMode;

  useEffect(() => {
    const themeVars = themes[resolvedTheme];
    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  }, [resolvedTheme]);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette:
          resolvedTheme === "dark"
            ? {
                mode: "dark",
                background: { default: "#0f172a", paper: "#1e293b" },
                // sx의 "grey.50" 등은 배경/테두리 용도로 쓰여서 다크에선 어두운 쪽으로 뒤집음
                grey: { 50: "#172033", 100: "#1e293b", 200: "#334155", 300: "#475569" },
              }
            : { mode: "light" },
      }),
    [resolvedTheme],
  );

  return (
    <ThemeContext.Provider value={{ theme: resolvedTheme, setTheme: () => {} }}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
